import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { axonLead } from "@/lib/axon";
import { gadsLead } from "@/lib/gads";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ArcadeOpsLogo from "@/components/ArcadeOpsLogo";
import { US_STATES } from "@/lib/states";
import { applyMeta, SITE_URL } from "@/lib/seo";
import { AogSignupView } from "@/pages/AogSignup";
import NotFound from "@/pages/NotFound";

const API = process.env.REACT_APP_BACKEND_URL;
export const THEMES = {
  dark: { bg: "#0b0b0d", fg: "#f4f4f5", muted: "#a1a1aa", card: "rgba(24,24,27,0.9)", accent: "#f5c243" },
  light: { bg: "#fafaf7", fg: "#111113", muted: "#55555e", card: "rgba(255,255,255,0.95)", accent: "#c8a300" },
  bold: { bg: "#160b2e", fg: "#ffffff", muted: "#d6c9ff", card: "rgba(0,0,0,0.45)", accent: "#39ff88" },
};

export function themeVars(c) {
  const t = THEMES[c?.landing_theme] || THEMES.dark;
  return { ...t, bg: c?.landing_bg_color || t.bg, accent: c?.landing_accent_color || t.accent };
}

const assetUrl = (u) => (u ? (u.startsWith("blob:") || u.startsWith("http") ? u : `${API}${u}`) : null);

// Public, prop-driven campaign landing (/:slug root-level canonical; /preview/:slug and legacy /c/:slug also resolve). Also used as the admin live preview.
export function CampaignLandingView({ c, slug, preview = false }) {
  const t = themeVars(c);
  const fields = c.custom_fields || [];
  const [f, setF] = useState({ business_name: "", contact_name: "", phone: "", email: "", state: "", message: "", business_type: c.business_type || "" });
  const [cr, setCr] = useState({});
  const [done, setDone] = useState(false);
  const blocked = f.state && (c.restricted_states || []).includes(f.state);
  const missingRequired = fields.some((x) => x.required && (x.type === "checkbox" ? !cr[x.key] : !(cr[x.key] || "").toString().trim()));
  const submit = async (e) => {
    e.preventDefault();
    if (preview) { toast.info("Preview only — the live page will send this."); return; }
    try { await api.post(`/campaigns/public/${slug}/inquire`, { ...f, custom_responses: cr }); axonLead("campaign_landing"); gadsLead(); setDone(true); }
    catch (x) { toast.error(x?.response?.data?.detail || "Couldn't send — try again."); }
  };
  const inp = { background: "transparent", color: t.fg, borderColor: `${t.fg}33` };
  return (
    <div className={preview ? "" : "min-h-screen"} style={{ background: t.bg, color: t.fg }} data-testid="campaign-landing-page" data-theme={c.landing_theme || "dark"}>
      <header className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${t.fg}1f` }}>
        {c.landing_logo_url ? <img data-testid="campaign-landing-logo" src={assetUrl(c.landing_logo_url)} alt={c.name} className="h-9 max-w-[200px] object-contain" /> : <ArcadeOpsLogo size={34} showWordmark />}
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>{c.name}</span>
      </header>
      <main className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 p-6 md:p-12">
        <section className="space-y-5">
          {c.landing_photo_url && <img data-testid="campaign-landing-photo" src={assetUrl(c.landing_photo_url)} alt={c.landing_headline || c.name} className="w-full max-h-72 object-cover border" style={{ borderColor: `${t.accent}66` }} />}
          <h1 className="font-heading font-black text-4xl sm:text-5xl leading-tight" data-testid="campaign-landing-headline" style={{ color: t.accent }}>{c.landing_headline || c.name}</h1>
          <p className="text-base whitespace-pre-wrap" style={{ color: t.muted }}>{c.landing_body || "Tell us about your venue and we'll be in touch."}</p>
          {c.states?.length > 0 && <p className="font-mono text-xs" style={{ color: t.muted }}>Available in: {c.states.join(", ")}</p>}
          {(c.contact_phone || c.contact_email) && <p className="text-sm">Questions? {c.contact_phone && <a style={{ color: t.accent }} href={`tel:${c.contact_phone}`}>{c.contact_phone}</a>} {c.contact_email && <a style={{ color: t.accent }} className="ml-2" href={`mailto:${c.contact_email}`}>{c.contact_email}</a>}</p>}
        </section>
        <section className="p-6 border" style={{ background: t.card, borderColor: `${t.fg}1f` }}>
          {done ? <div data-testid="campaign-inquiry-done" className="text-center py-10"><div className="font-heading font-black text-2xl" style={{ color: t.accent }}>Got it!</div><p className="text-sm mt-2" style={{ color: t.muted }}>We'll reach out shortly.</p></div> : (
            <form onSubmit={submit} className="space-y-3" data-testid="campaign-inquiry-form">
              <Input data-testid="ci-business" required placeholder="Business name" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} className="rounded-none" style={inp} />
              <Input data-testid="ci-business-type" placeholder={c.business_type ? `Business type (e.g. ${c.business_type})` : "Business type"} value={f.business_type} onChange={(e) => setF({ ...f, business_type: e.target.value })} className="rounded-none" style={inp} />
              <Input data-testid="ci-name" placeholder="Your name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className="rounded-none" style={inp} />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="ci-phone" placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-none" style={inp} />
                <Input data-testid="ci-email" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none" style={inp} />
              </div>
              <select data-testid="ci-state" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="w-full h-10 px-3 border text-sm" style={{ ...inp, background: t.bg }}>
                <option value="">State</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {blocked && <p data-testid="ci-blocked" className="text-xs text-red-400">Sorry — this offer isn't available in {f.state}.</p>}
              {fields.map((x) => (
                <label key={x.key} className="block text-xs font-mono uppercase tracking-widest" style={{ color: t.muted }} data-testid={`ci-custom-${x.key}`}>
                  {x.type !== "checkbox" && <span>{x.label}{x.required ? " *" : ""}</span>}
                  {x.type === "text" && <Input data-testid={`ci-custom-${x.key}-input`} required={x.required} value={cr[x.key] || ""} onChange={(e) => setCr({ ...cr, [x.key]: e.target.value })} className="rounded-none mt-1" style={inp} />}
                  {x.type === "select" && (
                    <select data-testid={`ci-custom-${x.key}-input`} required={x.required} value={cr[x.key] || ""} onChange={(e) => setCr({ ...cr, [x.key]: e.target.value })} className="w-full h-10 px-3 border text-sm mt-1" style={{ ...inp, background: t.bg }}>
                      <option value="">— choose —</option>{(x.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  {x.type === "checkbox" && (
                    <span className="inline-flex items-center gap-2 normal-case tracking-normal text-sm" style={{ color: t.fg }}>
                      <input type="checkbox" data-testid={`ci-custom-${x.key}-input`} checked={!!cr[x.key]} onChange={(e) => setCr({ ...cr, [x.key]: e.target.checked })} /> {x.label}{x.required ? " *" : ""}
                    </span>
                  )}
                </label>
              ))}
              <Textarea data-testid="ci-message" placeholder="Anything we should know?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="rounded-none" rows={3} style={inp} />
              <Button data-testid="ci-submit" type="submit" disabled={blocked || !(f.phone || f.email) || missingRequired} className="w-full rounded-none font-heading font-bold uppercase tracking-widest text-xs hover:opacity-90" style={{ background: t.accent, color: t.bg === THEMES.light.bg || c.landing_theme === "light" ? "#fff" : "#000" }}>Send</Button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

export default function CampaignLanding() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/campaigns/public/${slug}`).then((r) => setC(r.data)).catch(() => setErr("This campaign page isn't available.")); }, [slug]);
  useEffect(() => {
    if (!c) return;
    const body = (c.landing_body || "").replace(/\s+/g, " ").trim();
    applyMeta({
      title: `${c.landing_headline || c.name} | Arcade Ops`,
      description: body ? body.slice(0, 157) + (body.length > 157 ? "…" : "") : `${c.name} — a campaign by Arcade Ops.`,
      canonical: `${SITE_URL}/${slug}`,
      noindex: c.status !== "live",
    });
  }, [c, err, slug]);
  if (err) return <NotFound />;   // unknown root-level slug = a real 404 page (noindex), not a redirect
  if (!c) return null;
  if (c.landing_mode === "agent" && c.agent_landing_template === "aog_signup") return <AogSignupView campaign={c} slug={slug} />;
  return <CampaignLandingView c={c} slug={slug} />;
}
